import { isHighDPI } from '@trello/browser';
import {
  CanonicalCard,
  CanonicalAvatar as Avatar,
} from '@atlassian/trello-canonical-components';
import { forTemplate } from 'app/common/lib/util/i18n';
import {
  biggestPreview,
  smallestPreviewBiggerThan,
} from 'app/common/lib/util/preview';
import React from 'react';
import {
  BadgesModel,
  CoverPhotoModel,
  LabelColor,
  LabelModel,
  MemberModel,
  StickerModel,
  TeamModel,
} from 'app/gamma/src/types/models';
import { formatHumanDate } from '@trello/dates';
import {
  byAttributeCaseInsensitive,
  labelByColor,
} from 'app/gamma/src/util/sort';
import { HasNonNullKeys } from 'app/gamma/src/util/types';
import { LinkWrapper } from 'app/common/components/singlespa/link-wrapper';
import { CardTemplateBadge } from '@atlassian/trello-canonical-components/src/card-front/Badges';
import { featureFlagClient } from '@trello/feature-flag-client';

const {
  ArchivedBadge,
  AttachmentsBadge,
  Card,
  CardBadges,
  CardCover,
  CardDetails,
  CardLabel,
  CardLabels,
  CardLink: CardLinkComponent,
  CardMembers,
  CardStickers,
  CardTitle,
  ChecklistBadge,
  CommentsBadge,
  DescriptionBadge,
  DueDateBadge,
  NotificationBadge,
  Sticker,
  SubscribedBadge,
  TrelloAttachmentsBadge,
  VotesBadge,
} = CanonicalCard;

const MAX_COVER_HEIGHT = 260;
const DEFAULT_CARD_WIDTH = 245;
const DEFAULT_STICKER_AREA_HEIGHT = 64;

const format = forTemplate('canonical_card');

function showNewLabelPatterns() {
  return featureFlagClient.get('remarkable.new-color-blind-patterns', false);
}

function showCheckItemsEarliestDue() {
  return featureFlagClient.get('remarkable.advanced-checklists-v2', false);
}

const labelColorToColorBlindPattern: {
  [key in LabelColor]:
    | 'striped'
    | 'transparent'
    | 'slant-right'
    | 'slant-left'
    | 'checkered'
    | 'StripeDiagonal'
    | 'WaveSquare'
    | 'StripeVertical'
    | 'DotsChecker'
    | 'GrooveDiagonal'
    | 'StripeHorizontal'
    | 'BrickDiagonal'
    | 'Scale'
    | 'BrickHorizontal'
    | 'BrickVertical';
} = {
  black: showNewLabelPatterns() ? 'BrickVertical' : 'striped',
  blue: showNewLabelPatterns() ? 'StripeHorizontal' : 'transparent',
  green: showNewLabelPatterns() ? 'StripeDiagonal' : 'slant-right',
  lime: showNewLabelPatterns() ? 'Scale' : 'slant-left',
  orange: showNewLabelPatterns() ? 'StripeVertical' : 'striped',
  pink: showNewLabelPatterns() ? 'BrickHorizontal' : 'slant-right',
  purple: showNewLabelPatterns() ? 'GrooveDiagonal' : 'slant-left',
  red: showNewLabelPatterns() ? 'DotsChecker' : 'checkered',
  sky: showNewLabelPatterns() ? 'BrickDiagonal' : 'transparent',
  yellow: showNewLabelPatterns() ? 'WaveSquare' : 'checkered',
};

/*
 * Extract the height, img url, and background color/size for the card cover
 * from the CoverPhotoModel. Calculates the height dynamically based on the
 * cover dimensions and available space (which is not *actually* the available
 * space, but based on some given defaults). Has an escape hatch to override
 * the height with a fixed size for use-cases where that's needed - such as in
 * search results.
 */
const getCoverProps = (
  cover: CoverPhotoModel | null | undefined,
  heightOverride: number | undefined,
): {
  height: number;
  img: string;
  bgColor: string;
  bgSize: 'cover' | 'contain';
  sharedSourceUrl?: string;
} => {
  const preview = cover
    ? smallestPreviewBiggerThan(cover.previews, isHighDPI() ? 600 : 300) ||
      biggestPreview(cover.previews)
    : null;
  const details = {
    height: 0,
    img: '',
    bgColor: '',
    bgSize: 'cover' as 'cover' | 'contain',
    sharedSourceUrl: '' as string | undefined,
  };

  if (!cover || !preview) {
    return details;
  }

  details.img = preview.url ? preview.url : cover.url;
  details.sharedSourceUrl = cover.sharedSourceUrl;
  details.bgColor = cover.edgeColor;

  const max = Math.min(preview.height, MAX_COVER_HEIGHT);
  const scaled = (preview.height * DEFAULT_CARD_WIDTH) / preview.width;
  if (scaled <= max) {
    details.height = scaled;
    details.bgSize = 'cover';
  } else {
    details.height = max;
    details.bgSize = 'contain' as 'cover' | 'contain';
  }

  // Let above run based on default sizing and *then* override
  details.height = heightOverride ? heightOverride : details.height;

  return details;
};

interface CardContentsProps {
  readonly name?: string;
  readonly badges?: BadgesModel;
  readonly team?: TeamModel | null;
  readonly members?: MemberModel[];
  readonly colorBlind?: boolean;
  readonly labels?: LabelModel[];
  readonly cover?: CoverPhotoModel | null;
  readonly coverHeight?: number;
  readonly archived?: boolean;
  readonly isTemplate?: boolean;
  readonly stickers?: StickerModel[];
  readonly numNotifications?: number;
  readonly expandLabels?: boolean;
  readonly replacements?: {
    CardTitle?: React.ReactElement;
  };
}

export const CardContents: React.FunctionComponent<CardContentsProps> = ({
  name,
  team,
  members = [],
  labels = [],
  colorBlind,
  cover,
  coverHeight,
  archived,
  badges,
  isTemplate,
  stickers = [],
  expandLabels,
  numNotifications,
  replacements = {},
}) => {
  const trelloAttachments =
    badges && badges.attachmentsByType
      ? badges.attachmentsByType.trello.board +
        badges.attachmentsByType.trello.card
      : 0;
  const attachments = badges ? badges.attachments - trelloAttachments : 0;
  const coverProps = getCoverProps(cover, coverHeight);
  const deactivatedMembers = new Set(
    team && team.memberships
      ? team.memberships
          .filter((membership) => membership.deactivated)
          .map((membership) => membership.idMember)
      : [],
  );

  return (
    <>
      {coverProps.img && <CardCover {...coverProps} />}
      {stickers.length > 0 && (
        <CardStickers
          height={
            coverProps.height ? coverProps.height : DEFAULT_STICKER_AREA_HEIGHT
          }
        >
          {stickers.map((sticker) => (
            <Sticker
              key={sticker.id}
              left={sticker.left}
              top={sticker.top}
              alt={sticker.image}
              img={
                isHighDPI() && sticker.imageUrl2x
                  ? sticker.imageUrl2x
                  : sticker.imageUrl
              }
              rotate={sticker.rotate}
              zIndex={sticker.zIndex}
            />
          ))}
        </CardStickers>
      )}
      <CardDetails>
        {labels && labels.length > 0 && (
          <CardLabels>
            {labels
              .filter((label) => !!label.color)
              .sort(labelByColor)
              .map((label: HasNonNullKeys<LabelModel, 'color'>) => (
                <CardLabel
                  key={label.id}
                  color={label.color}
                  pattern={
                    colorBlind
                      ? labelColorToColorBlindPattern[label.color]
                      : null
                  }
                >
                  {expandLabels ? label.name || ' ' : null}
                </CardLabel>
              ))}
          </CardLabels>
        )}
        {replacements.CardTitle ? (
          replacements.CardTitle
        ) : (
          <CardTitle>{name}</CardTitle>
        )}
        <CardBadges>
          {isTemplate && (
            <CardTemplateBadge>
              {format('card template badge label')}
            </CardTemplateBadge>
          )}
          {!!numNotifications && (
            <NotificationBadge numUnread={numNotifications} />
          )}
          {badges && badges.subscribed && <SubscribedBadge />}
          {badges && badges.votes > 0 && !isTemplate && (
            <VotesBadge
              numVotes={badges.votes}
              voted={badges.viewingMemberVoted}
            />
          )}
          {badges && badges.due && !isTemplate && (
            <DueDateBadge
              dueDate={new Date(badges.due)}
              isComplete={badges.dueComplete}
            >
              {formatHumanDate(badges.due)}
            </DueDateBadge>
          )}
          {badges && badges.description && <DescriptionBadge />}
          {badges && badges.comments > 0 && (
            <CommentsBadge numComments={badges.comments} />
          )}
          {attachments > 0 && <AttachmentsBadge numAttachments={attachments} />}
          {trelloAttachments > 0 && (
            <TrelloAttachmentsBadge numAttachments={trelloAttachments} />
          )}
          {badges && badges.checklistItems > 0 && (
            <ChecklistBadge
              numItems={badges.checklistItems}
              numComplete={badges.checklistItemsChecked}
              checkItemsEarliestDue={
                showCheckItemsEarliestDue()
                  ? badges.checkItemsEarliestDue
                  : null
              }
            />
          )}
          {archived && !isTemplate && (
            <ArchivedBadge>{format('archived card badge label')}</ArchivedBadge>
          )}
        </CardBadges>
        {members && members.length > 0 && (
          <CardMembers>
            {members
              .sort(byAttributeCaseInsensitive('name'))
              .reduceRight((result: JSX.Element[], member) => {
                result.push(
                  <Avatar
                    key={member.id}
                    avatarSource={member.avatarSource}
                    deactivated={deactivatedMembers.has(member.id)}
                    img={member.avatars && member.avatars[30]}
                    initials={member.initials}
                    lightBackground
                  />,
                );

                return result;
              }, [])}
          </CardMembers>
        )}
      </CardDetails>
    </>
  );
};

export interface CardFrontProps extends CardContentsProps {
  className?: string;
  style?: React.CSSProperties;
}

export const CardFront = ({ className, style, ...props }: CardFrontProps) => (
  <Card className={className} style={style}>
    <CardContents {...props} />
  </Card>
);

export interface CardLinkProps extends CardContentsProps {
  className?: string;
  url: string;
}

export const CardLink = ({ url, className, ...props }: CardLinkProps) => (
  <CardLinkComponent
    className={className}
    href={url}
    linkComponent={LinkWrapper}
  >
    <CardContents {...props} />
  </CardLinkComponent>
);
